import { Grid, Typography, Alert } from '@mui/material';
import { RslReport } from '../../../api/schema';
import { formatters, ValueInput } from '@utils/formattersV2';
import { WaterfallChartWithHeader } from '../../shared/WaterfallChartWithHeader';
import { ColumnChartWithHeader } from '../../shared/ColumnChartWithHeader';
import { channelNames } from '../../../constants/channels';
import { getReportChannels } from '../../../hooks/getReportChannels';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { useRslAIInsights } from '../../../hooks/useRslAIInsights';

type ContributionMetricsProps = {
  reports: RslReport[];
  selectedReport: RslReport | null;
  highlightsComponent?: React.ReactNode;
};

export const ContributionMetrics = (props: ContributionMetricsProps) => {
  const { selectedReport } = props;

  const { rslAiInsights: contributionAiInsights } = useRslAIInsights(
    selectedReport,
    'contribution-metrics',
    null
  );

  if (!selectedReport) {
    return <Alert severity="error">No purchase data available</Alert>;
  }

  const { inStoreChannel, onlineChannel } = getReportChannels(selectedReport);

  const channels = [
    {
      type: 'combined' as const,
      data: selectedReport,
      confidenceLevel: selectedReport.incrementalSalesLiftConfidenceLevel,
      testId: 'rsl-total-waterfall-chart',
    },
    {
      type: 'in_store' as const,
      data: inStoreChannel,
      confidenceLevel: inStoreChannel?.incrementalSalesLiftConfidenceLevel,
      testId: 'rsl-in-store-waterfall-chart',
    },
    {
      type: 'online' as const,
      data: onlineChannel,
      confidenceLevel: onlineChannel?.incrementalSalesLiftConfidenceLevel,
      testId: 'rsl-online-waterfall-chart',
    },
  ];

  return (
    <Grid container spacing="1.5rem">
      <Grid mt="1rem" size={12}>
        <AIInsightsCard
          testId="rsl-contribution-metrics-ai-insights"
          title="Summary"
          aiInsights={contributionAiInsights}
          highlights={props.highlightsComponent}
        />
      </Grid>

      <Grid size={12}>
        <Typography fontStyle="italic" textAlign="right">
          Metrics are not additive due to interaction effects
        </Typography>
      </Grid>

      {channels.map(channel => (
        <Grid
          key={channel.type}
          size={{
            lg: 4,
            xs: 12,
          }}
        >
          <WaterfallChartWithHeader
            title={channelNames[channel.type]}
            chartProps={{
              yAxisFormatter: (value: ValueInput) =>
                formatters.percentage(value, { decimals: 1 }),
              valueFormatter: (value: ValueInput) =>
                formatters.percentage(value, { decimals: 1 }),
              data: [
                {
                  name: 'Transaction Size',
                  value: channel.data?.transactionSizeLift ?? 0,
                  dataLabelValue: channel.data?.transactionSizeLift ?? 0,
                },
                {
                  name: 'Transaction Rate',
                  value: channel.data?.transactionRateLift ?? 0,
                  dataLabelValue: channel.data?.transactionRateLift ?? 0,
                },
                {
                  name: 'Penetration',
                  value: channel.data?.penetrationLift ?? 0,
                  dataLabelValue: channel.data?.penetrationLift ?? 0,
                },
                {
                  name: 'Incrementality',
                  value: channel.data?.incrementalSalesLift ?? 0,
                  dataLabelValue: channel.data?.incrementalSalesLift ?? 0,
                },
              ],
            }}
            value={channel.data?.incrementalSalesLift ?? 0}
            valueFormatter={(value: ValueInput) =>
              formatters.lift(value, { decimals: 1 })
            }
            confidenceLevel={channel.confidenceLevel}
            data-testid={channel.testId}
          />
        </Grid>
      ))}

      {[
        {
          title: 'Transaction Size',
          yAxisValueFormatter: formatters.currency,
          valueFormatter: (value: ValueInput) =>
            formatters.currency(value, { decimals: 2 }),
          data: [
            {
              name: channelNames.combined,
              value: selectedReport.transactionSize ?? 0,
            },
            {
              name: channelNames.in_store,
              value: inStoreChannel?.transactionSize ?? 0,
            },
            {
              name: channelNames.online,
              value: onlineChannel?.transactionSize ?? 0,
            },
          ],
          testId: 'rsl-transaction-size-chart',
        },
        {
          title: 'Transaction Rate',
          yAxisValueFormatter: formatters.number,
          yAxisMinTickInterval: 1,
          valueFormatter: (value: ValueInput) =>
            formatters.number(value, { decimals: 1 }),
          data: [
            {
              name: channelNames.combined,
              value: selectedReport.transactionRate ?? 0,
            },
            {
              name: channelNames.in_store,
              value: inStoreChannel?.transactionRate ?? 0,
            },
            {
              name: channelNames.online,
              value: onlineChannel?.transactionRate ?? 0,
            },
          ],
          testId: 'rsl-transaction-rate-chart',
        },
        {
          title: 'Penetration',
          yAxisValueFormatter: formatters.percentage,
          yAxisMinTickInterval: 0.01,
          valueFormatter: (value: ValueInput) =>
            formatters.percentage(value, { decimals: 1 }),
          data: [
            {
              name: channelNames.combined,
              value: selectedReport.penetration ?? 0,
            },
            {
              name: channelNames.in_store,
              value: inStoreChannel?.penetration ?? 0,
            },
            {
              name: channelNames.online,
              value: onlineChannel?.penetration ?? 0,
            },
          ],
          testId: 'rsl-penetration-chart',
        },
      ].map(metric => (
        <Grid
          key={metric.testId}
          size={{
            lg: 4,
            xs: 12,
          }}
        >
          <ColumnChartWithHeader
            title={metric.title}
            chartProps={{
              yAxisValueFormatter: metric.yAxisValueFormatter,
              yAxisMinTickInterval: metric.yAxisMinTickInterval,
              valueFormatter: metric.valueFormatter,
              data: metric.data,
            }}
            data-testid={metric.testId}
          />
        </Grid>
      ))}
    </Grid>
  );
};
